/**
 * THE STORE VIEW — a demo preview of the grocer's simple console.
 *
 * Six big one-tap buttons and nothing else. No prices, no payment, no PIN:
 * the grocer's full console is a later phase, and this page exists so a
 * showroom can watch the family's status bar move while someone taps here.
 *
 * It is labelled as a demo preview on screen, because that is what it is.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Store, Clock, PackageCheck, Truck, Check, ShoppingBasket, AlertTriangle, Home, Receipt } from 'lucide-react';
import { Button } from '../../components/ui/button';
import LanguageToggle from '../../components/LanguageToggle';
import GroceryTextPreview from '../../components/grocery/GroceryTextPreview';
import GroceryModeToggle from '../../components/grocery/GroceryModeToggle';

const API = (typeof window !== 'undefined' ? window.location.origin : '');
const STORE_KEY = 'yulontap_grocery_store';
const POLL_MS = 5000;

const ICON = {
    accepted: PackageCheck, picking: ShoppingBasket, ready: Check,
    out_for_delivery: Truck, delivered: Check, delayed: AlertTriangle,
};

const Line = ({ ln, t }) => (
    <li className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-foreground" aria-hidden="true">•</span>
        <span className="break-words font-semibold">{ln.name} ({ln.qty || 1})</span>
        {ln.options?.length ? (
            <span className="break-words text-xs text-muted-foreground"
                data-testid={`grocery-store-line-options-${ln.id}`}>· {ln.options.join(' · ')}</span>
        ) : null}
        {ln.note ? (
            <span className="break-words text-xs font-semibold text-sky-700 dark:text-sky-300"
                data-testid={`grocery-store-line-note-${ln.id}`}>· {ln.note}</span>
        ) : null}
    </li>
);

export default function GroceryStorePage() {
    const { t, i18n } = useTranslation();
    const [token, setToken] = useState(null);
    const [data, setData] = useState(null);
    const [failed, setFailed] = useState(false);
    const [busy, setBusy] = useState(null);

    const start = useCallback(async () => {
        setFailed(false);
        try {
            let saved = null;
            try { saved = sessionStorage.getItem(STORE_KEY); } catch (_e) { /* ignore */ }
            if (saved) {
                const probe = await fetch(`${API}/api/grocery/store/orders`,
                    { headers: { 'X-Grocery-Store-Session': saved } });
                if (probe.ok) { setToken(saved); return; }
            }
            const r = await fetch(`${API}/api/demo-grocery/store/session`);
            if (!r.ok) throw new Error(`http_${r.status}`);
            const d = await r.json();
            if (!d?.store_session) throw new Error('no_session');
            setToken(d.store_session);
            try { sessionStorage.setItem(STORE_KEY, d.store_session); } catch (_e) { /* ignore */ }
        } catch (_e) { setFailed(true); }
    }, []);

    useEffect(() => { start(); }, [start]);

    const load = useCallback(() => {
        if (!token) return;
        fetch(`${API}/api/grocery/store/orders`, { headers: { 'X-Grocery-Store-Session': token } })
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => { if (d) { setData(d); setFailed(false); } else setFailed(true); })
            .catch(() => setFailed(true));       // the last good list stays on screen
    }, [token]);

    useEffect(() => { load(); const id = setInterval(load, POLL_MS); return () => clearInterval(id); }, [load]);

    const step = async (orderId, status) => {
        setBusy(`${orderId}:${status}`);
        try {
            await fetch(`${API}/api/grocery/store/orders/${orderId}/status`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Grocery-Store-Session': token },
                body: JSON.stringify({ status }),
            });
            load();
        } finally { setBusy(null); }
    };

    if (!token || !data) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-50 dark:bg-slate-950">
                {failed ? (
                    <>
                        <p className="text-sm font-semibold" data-testid="grocery-store-load-failed">
                            {t('grocery.couldNotLoad')}
                        </p>
                        <Button onClick={() => { start(); load(); }} className="min-h-[44px]"
                            data-testid="grocery-store-load-retry">{t('grocery.tryAgain')}</Button>
                    </>
                ) : <Loader2 className="h-7 w-7 animate-spin text-emerald-400" />}
            </div>
        );
    }

    const orders = data.orders || [];

    return (
        <div className="min-h-screen bg-slate-50 pb-32 text-[#13265C] dark:bg-slate-950 dark:text-slate-100" data-testid="grocery-store-page">
            <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 px-4 py-3 backdrop-blur dark:border-white/10 dark:bg-slate-950/90">
                <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                        <p className="flex items-center gap-2 text-base font-bold" data-testid="grocery-store-name">
                            <Store className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />{data.store_name}
                        </p>
                        <p className="text-xs font-semibold text-[#13265C]/70 dark:text-[#FFC08A]" data-testid="grocery-store-demo-label">
                            {t('groceryStore.demoLabel')}
                        </p>
                    </div>
                    <LanguageToggle />
                    <GroceryModeToggle memberKey="fridge" testId="grocery-mode-toggle-store" compact />
                </div>
            </header>

            <main className="space-y-4 p-4">
                {!orders.length ? (
                    <p className="py-16 text-center text-sm text-muted-foreground" data-testid="grocery-store-empty">
                        {t('groceryStore.empty')}
                    </p>
                ) : null}

                {orders.map((o) => (
                    <section key={o.id} className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-900"
                        data-testid={`grocery-store-order-${o.id}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <p className="text-base font-bold" data-testid={`grocery-store-household-${o.id}`}>
                                    {o.household_name || t('groceryStore.household')}
                                </p>
                                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                    <Clock className="h-3.5 w-3.5" />
                                    {o.window ? `${new Date(`${o.window.day}T12:00:00`).toLocaleDateString(i18n.language,
                                        { weekday: 'short', day: 'numeric', month: 'short' })} · ${o.window.start}–${o.window.end}` : ''}
                                </p>
                            </div>
                            <span data-testid={`grocery-store-billing-badge-${o.id}`}
                                className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-bold ${
                                    o.billing_mode === 'separate_bills'
                                        ? 'border-[#F58220] text-[#13265C] dark:text-[#F58220]'
                                        : 'border-slate-300 text-muted-foreground dark:border-white/20'}`}>
                                {o.billing_mode === 'separate_bills'
                                    ? t('groceryStore.billSeparate') : t('groceryStore.billOne')}
                            </span>
                            <span data-testid={`grocery-store-status-${o.id}`}
                                className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${
                                    o.status === 'delayed'
                                        ? 'bg-[#F58220] text-[#13265C]'
                                        : 'bg-emerald-700 text-white dark:bg-emerald-500/15 dark:text-emerald-200'}`}>
                                {t(`groceryStore.status.${o.status}`)}
                            </span>
                        </div>

                        {o.billing_mode === 'separate_bills' && (o.billing_groups || []).length ? (
                            /* ONE ORDER STILL — one pick list, one delivery. The
                               grouping is only so the grocer can bill it. */
                            <div className="mt-3 space-y-3" data-testid={`grocery-store-lines-${o.id}`}>
                                {o.billing_groups.map((g) => (
                                    <div key={g.member_id || 'shared'}
                                        data-testid={`grocery-store-bill-group-${o.id}-${g.member_id || 'shared'}`}
                                        className={`rounded-xl border p-2.5 ${g.shared
                                            ? 'border-dashed border-slate-400 dark:border-slate-500'
                                            : 'border-slate-200 dark:border-white/10'}`}>
                                        <p className="flex flex-wrap items-center gap-x-2 text-sm font-bold">
                                            {g.shared ? <Home className="h-4 w-4" /> : null}
                                            {g.shared ? t('groceryStore.billShared') : g.name}
                                            <span className="text-xs font-semibold text-muted-foreground">
                                                {t('groceryStore.billItems', { count: g.item_count })}
                                            </span>
                                            {g.shared ? (
                                                <span className="text-xs font-semibold text-muted-foreground">
                                                    · {t('groceryStore.billSplitEvenly')}
                                                </span>
                                            ) : null}
                                            {g.routed_to_owner ? (
                                                <span className="flex items-center gap-1 text-xs font-semibold text-amber-700 dark:text-amber-300"
                                                    data-testid={`grocery-store-bill-missing-${g.member_id}`}>
                                                    <AlertTriangle className="h-3.5 w-3.5" />
                                                    {t('groceryStore.billMissingContact', { owner: g.routed_to_name })}
                                                </span>
                                            ) : (
                                                <span className="text-xs text-muted-foreground">
                                                    · {g.bill_email || g.bill_phone}
                                                </span>
                                            )}
                                        </p>
                                        <ul className="mt-1 space-y-1">
                                            {(g.lines || []).map((ln) => <Line key={ln.id} ln={ln} t={t} />)}
                                        </ul>
                                    </div>
                                ))}
                                <p className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground"
                                    data-testid={`grocery-store-bill-demo-${o.id}`}>
                                    <Receipt className="h-3.5 w-3.5" />{t('groceryStore.billDemoNote')}
                                </p>
                            </div>
                        ) : (
                            <ul className="mt-3 space-y-1" data-testid={`grocery-store-lines-${o.id}`}>
                                {o.lines.map((ln) => <Line key={ln.id} ln={ln} t={t} />)}
                            </ul>
                        )}

                        <div className={`mt-4 grid gap-2 ${(o.next || []).length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                            {(o.next || []).map((s) => {
                                const Icon = ICON[s] || Check;
                                return (
                                    <Button key={s} onClick={() => step(o.id, s)}
                                        disabled={busy === `${o.id}:${s}`}
                                        data-testid={`grocery-store-btn-${s}`}
                                        className={`min-h-[64px] gap-2 text-base font-bold ${
                                            s === 'delayed'
                                                ? 'bg-[#F58220] text-[#13265C] hover:bg-[#FF9A44]'
                                                : 'bg-emerald-700 text-white hover:bg-emerald-600'}`}>
                                        {busy === `${o.id}:${s}` ? <Loader2 className="h-5 w-5 animate-spin" />
                                            : <Icon className="h-5 w-5" />}
                                        {t(`groceryStore.act.${s}`)}
                                    </Button>
                                );
                            })}
                            {!(o.next || []).length ? (
                                <p className="col-span-2 text-sm text-muted-foreground" data-testid={`grocery-store-done-${o.id}`}>
                                    {t('groceryStore.allDone')}
                                </p>
                            ) : null}
                        </div>
                    </section>
                ))}

                <GroceryTextPreview storeSession={token} side="store" />
            </main>
        </div>
    );
}
